import React, {useEffect, useState} from "react";
import apiRequest from "../../services/apiRequest";
import {ChartBarIcon, ClipboardDocumentIcon, HomeModernIcon, TrophyIcon, UserIcon} from "@heroicons/react/24/outline";
import {
  VisibilityLevel,
  LkpLevelOptions,
  VisibilityType,
  InternalUser,
  PeriodType,
  CustomPeriod,
  WorksiteOwnershipType,
  VisibilityRoles
} from "./Analytics.type";
import {IntlShape} from "react-intl";
import moment from "moment";
import {PRIVILEGED_ROLES} from "../../constants";

export const getSectionIcon = (section: string) => {
  switch (section) {
    case 'worksites':
      return HomeModernIcon
    case 'configurations':
      return ClipboardDocumentIcon
    case 'products':
      return ChartBarIcon
    case 'game':
      return TrophyIcon
    default:
      return UserIcon
  }
}

export const getVisibilityLevel = (roleType: string) => {
  if (PRIVILEGED_ROLES.includes(roleType)) {
    return 1
  }
  if (['field', 'sales_director'].includes(roleType)) {
    return 2
  }
  if (roleType === 'area_manager') {
    return 3
  }
  return 4
}

const lkpLevelOptions: LkpLevelOptions[] = [
  // {name: "visibility_full", levels: [1, 2, 3]},
  // {name: "visibility_field_manager", levels: [1]},
  {name: "visibility_field", levels: [1]},
  {name: "visibility_sales_director", levels: [1]},
  {name: "visibility_area_manager", levels: [1, 2]},
  {name: "visibility_agent", levels: [1, 2, 3], excludedTabIndexes: [5]},
]


export const getVisibilityOptions = (visibilityLevel: VisibilityLevel, tabIndex:number, intl: IntlShape) => {
  const options = lkpLevelOptions.filter(i => i.levels.includes(visibilityLevel) && !i.excludedTabIndexes?.includes(tabIndex) ).map(i => i.name)
  return options.map((i) => ({
    value: i,
    name: intl.formatMessage({id: i}),
  }))
}

export const getVisibilityRoles = (roleType: string):VisibilityRoles[] => {
  if ((PRIVILEGED_ROLES.concat(["field"]).includes(roleType))){
    return ["field_manager", "field", "area_manager", "agent"]
  }
  if (roleType === "sales_director") {
    return ["field", "area_manager", "agent"]
  }
  if (roleType === "area_manager") {
    return ["agent"]
  }
  return []
}

export const getVisibilityGroups = (roleType: string):VisibilityRoles[][] => {
  if ((PRIVILEGED_ROLES.concat(["field", "sales_director"]).includes(roleType))){
    return [["field_manager", "field"], ["area_manager", "agent"]]
  }
  if (roleType === "area_manager") {
    return [["agent"]]
  }
  return []
}
export const useInternalUsers = (countryCode: string, permission: string) => {

  const [users, setUsers] = useState();

  useEffect(() => {
    const fetchInternalUsers = () => {
      apiRequest
        .get(`/users/internal?country=${countryCode}`)
        .then((res: any) => {
          setUsers(res);
        })
        .catch((err) => {
          console.log("errore", err)
        })
    }
    if (!users) {
      fetchInternalUsers()
    }
  }, [countryCode]) // eslint-disable-line react-hooks/exhaustive-deps

  return users
}


export function arraysOverlap<T>(arr1: T[], arr2: T[]) {
  // Convert one array to a Set for faster lookup
  const set1 = new Set(arr1);

  // Check if any element in arr2 exists in set1
  for (const item of arr2) {
    if (set1.has(item)) {
      return true; // Overlap found
    }
  }

  return false; // No overlap
}


export const getUsersOptions = (visibility: VisibilityType, internalUsers: InternalUser[] , intl: IntlShape, visibilityUsers: {label: string; value: number}[] | undefined) => {

  const filteredUsers = internalUsers
    .filter(i => {
      return i.role.name === visibility.replace('visibility_', '') && (!visibilityUsers || visibilityUsers.length===0 ||
        arraysOverlap(i.role_hierarchy.split(';'), visibilityUsers.map(u => `user_${u.value}`))
      )
    })
    .map(i => ({value: i.id, label: `${i.lastname} ${i.firstname}`}))
    .sort((a, b) => a.label.localeCompare(b.label))
  // return [{value: 0, label: intl.formatMessage({id: "all"})}, ...filteredUsers]
  return filteredUsers
}


export const getAnalysisPeriod = (period: PeriodType, customPeriod: CustomPeriod) => {
  if (period === 'custom') {
    return {
      start: moment.utc(customPeriod.start).local().format('YYYY-MM-DD'),
      end: moment.utc(customPeriod.end).local().format('YYYY-MM-DD'),
    }
  }
  let today = new Date();
  let startDate = today.setMonth(today.getMonth() - 1);
  if (period === 'last_week') {
    let today = new Date();
    startDate = today.setDate(today.getDate() - 7)
  }
  if (period === 'last_year') {
    let today = new Date();
    startDate = today.setFullYear(today.getFullYear() - 1)
  }
  if (period === 'next_month') {
    let today = new Date();
    let startDate = today.setMonth(today.getMonth());
    let endDate = today.setMonth(today.getMonth() + 1);
    return {start: moment(startDate).format('YYYY-MM-DD'), end: moment(endDate).format('YYYY-MM-DD')}
  }
  if (period === 'next_year') {
    let today = new Date();
    let startDate = today.setFullYear(today.getFullYear());
    let endDate = today.setFullYear(today.getFullYear() + 1);
    return {start: moment(startDate).format('YYYY-MM-DD'), end: moment(endDate).format('YYYY-MM-DD')}
  }
  return {start: moment(startDate).format('YYYY-MM-DD'), end: moment(new Date()).format('YYYY-MM-DD')}
}

export const getPeriodOptions = (tabIndex:number) => {
  // resales needs data in the future
  if (tabIndex === 4) {
    return [
      'next_month',
      'next_year',
      'custom'
    ]
  } else {
    return [
      'last_week',
      'last_month',
      'last_year',
      'custom'
    ]
  }
}